import { Download, FileDown, RefreshCw } from "lucide-react";
import { FormEvent, useState } from "react";
import { Badge, formatDate } from "../../components/Badge";
import { Field, Select, Textarea } from "../../components/Form";
import { Empty, ErrorState, Failure, Loading, Success } from "../../components/State";
import { api, json, normalizePage } from "../../lib/api";
import type { Page } from "../../lib/types";
import { useAsync } from "../../lib/useAsync";

interface ExportJob {
  id: string;
  kind: string;
  status: string;
  purpose_note: string;
  row_count?: number;
  download_url?: string;
  download_expires_at?: string;
  download_count: number;
  max_download_count: number;
  attempt_count: number;
  last_error?: string;
  created_at: string;
}

const labels: Record<string, string> = {
  applications: "入驻申请",
  admissions: "入驻资格",
  audit_logs: "审计日志",
  policy_versions: "政策版本",
};

export function AdminExportsPage() {
  const [revision, setRevision] = useState(0);
  const [kind, setKind] = useState("applications");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string }>();
  const result = useAsync(
    async () => normalizePage(await api<Page<ExportJob>>("/admin/exports?pageSize=50")),
    [revision],
  );
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(undefined);
    try {
      await api("/admin/exports", {
        method: "POST",
        body: json({ kind, purposeNote: note, rowLimit: 50000, columns: [] }),
      });
      setNote("");
      setMessage({ ok: true, text: "导出任务已进入队列" });
      setRevision((value) => value + 1);
    } catch (error) {
      setMessage({ ok: false, text: error instanceof Error ? error.message : "创建导出失败" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>CONTROLLED EXPORT</span>
          <h1>数据导出</h1>
          <p>创建脱敏导出任务并查看受限下载结果。</p>
        </div>
        <button className="icon-button" title="刷新任务" aria-label="刷新任务" onClick={() => setRevision((value) => value + 1)}>
          <RefreshCw size={18} />
        </button>
      </header>
      <form className="admin-filterbar export-create" onSubmit={submit}>
        <Field label="数据范围" required>
          <Select value={kind} onChange={(event) => setKind(event.target.value)}>
            {Object.entries(labels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </Select>
        </Field>
        <Field label="导出用途" required>
          <Textarea value={note} minLength={5} maxLength={1024} required onChange={(event) => setNote(event.target.value)} />
        </Field>
        <button className="primary-button" disabled={busy}><FileDown size={17} />创建导出</button>
      </form>
      {message && (message.ok ? <Success>{message.text}</Success> : <Failure>{message.text}</Failure>)}
      {result.loading ? <Loading /> : result.error ? (
        <ErrorState error={result.error} retry={result.retry} />
      ) : !result.data?.items.length ? <Empty title="暂无导出任务" /> : (
        <div className="table-wrap export-table">
          <table>
            <thead><tr><th>数据范围</th><th>用途</th><th>状态</th><th>结果</th><th>创建时间</th><th>操作</th></tr></thead>
            <tbody>{result.data.items.map((item) => (
              <tr key={item.id}>
                <td><strong>{labels[item.kind] || item.kind}</strong><small>{item.id}</small></td>
                <td>{item.purpose_note}</td>
                <td><Badge tone={item.status === "succeeded" ? "success" : item.status === "failed" ? "danger" : "warning"}>{item.status}</Badge></td>
                <td><strong>{item.row_count ?? "-"} 行</strong><small>{item.download_count}/{item.max_download_count} 次下载</small></td>
                <td>{formatDate(item.created_at)}</td>
                <td>{item.download_url ? (
                  <a className="secondary-button compact" href={item.download_url}><Download size={15} />下载</a>
                ) : <span className="muted-text">{item.last_error || "处理中"}</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
